/**
 * useDailyShipmentsMonth — 특정 연·월의 일별 출하 집계(daily_shipments) 맵.
 *
 *   캘린더 위젯이 표시 중인 달의 날짜별 출하량을 표기하고, 날짜를 누르면 그날 요약
 *   (관급/사급/예정/회전수)을 보여줄 때 사용.
 *   연도 단위 listByYear 결과를 year별 in-memory 캐시 → 같은 연도 내 월 이동 0 reads.
 *
 *   반환
 *     byDay:    Map<dayOfMonth(1~31), total_m3>. 출하 없는 날은 키 부재.
 *     byDayDoc: Map<dayOfMonth, DailyShipment>. 문서가 있는 날(예정만 있는 날 포함).
 *   daily_shipments 의미: gov_m3/cust_m3 = 실제 출하량(shipped 합), *_planned_m3 = 예정.
 */
import { useEffect, useMemo, useState } from "react";
import { dailyShipmentsRepo } from "@/services/daily_shipments.repository";
import type { DailyShipment } from "@/domain/daily_shipments";
import type { TenantKey } from "@/domain/space";
import { useDataTenant } from "@/hooks/useTenantPath";

/** 캐시 키 = "법인:연도"(2026-10-20 1단계 — 법인 섞임 방지). 조회는 아직 중앙 컬렉션(2단계에서 tenant 경로로) */
const yearCache = new Map<string, DailyShipment[]>();

export interface UseDailyShipmentsMonthState {
  /** dayOfMonth(1~31) → 실제 출하 총량(㎥). 출하 0/없는 날은 키 부재 */
  byDay: Map<number, number>;
  /** dayOfMonth(1~31) → 그날 집계 문서 (실적 0 이어도 예정이 있으면 존재) */
  byDayDoc: Map<number, DailyShipment>;
  loading: boolean;
  error: string | null;
}

interface MonthMaps {
  byDay: Map<number, number>;
  byDayDoc: Map<number, DailyShipment>;
}

const EMPTY: MonthMaps = { byDay: new Map(), byDayDoc: new Map() };

function buildMonthMaps(docs: DailyShipment[], year: number, month: number): MonthMaps {
  const prefix = `${year}-${String(month).padStart(2, "0")}-`;
  const byDay = new Map<number, number>();
  const byDayDoc = new Map<number, DailyShipment>();
  for (const d of docs) {
    if (!d.id.startsWith(prefix)) continue;
    const day = Number(d.id.slice(prefix.length, prefix.length + 2));
    if (!Number.isFinite(day) || day < 1 || day > 31) continue;
    byDayDoc.set(day, d);
    if (d.total_m3 <= 0) continue;
    byDay.set(day, (byDay.get(day) ?? 0) + d.total_m3);
  }
  return { byDay, byDayDoc };
}

/**
 * @param year 4자리 연도 / @param month 1~12. enabled=false면 fetch 안 함(빈 맵).
 * @param tenant 데이터 법인. 생략 = 지금 공간의 데이터 법인(useDataTenant). 내 업무 카드처럼 법인이 정해진 곳은 명시.
 */
export function useDailyShipmentsMonth(
  year: number,
  month: number,
  enabled = true,
  tenant?: TenantKey,
): UseDailyShipmentsMonthState {
  const dataTenant = useDataTenant();
  const ck = `${tenant ?? dataTenant}:${year}`;
  // 그 연도의 문서 목록만 state 로 두고, 월 맵은 파생(useMemo) — effect 안 동기 setState 회피
  const [docs, setDocs] = useState<{ ck: string; list: DailyShipment[] } | null>(() => {
    const cached = yearCache.get(ck);
    return cached ? { ck, list: cached } : null;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 캐시 히트면 useMemo 가 yearCache 를 직접 읽으므로 할 일이 없다
    if (!enabled || yearCache.has(ck)) return;
    let cancelled = false;
    dailyShipmentsRepo
      .listByYear(year)
      .then((list) => {
        if (cancelled) return;
        yearCache.set(ck, list);
        setDocs({ ck, list });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [year, ck, enabled]);

  const maps = useMemo<MonthMaps>(() => {
    if (!enabled) return EMPTY;
    const list = docs && docs.ck === ck ? docs.list : yearCache.get(ck);
    return list ? buildMonthMaps(list, year, month) : EMPTY;
  }, [docs, ck, year, month, enabled]);

  // 로딩 = 켜져 있고, 그 연도 문서가 state 에도 캐시에도 아직 없음 (실패했으면 로딩 아님)
  const loading = enabled && !error && !(docs && docs.ck === ck) && !yearCache.has(ck);
  return { byDay: maps.byDay, byDayDoc: maps.byDayDoc, loading, error };
}
